import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import LoadingSpinner from './LoadingSpinner';

/**
 * A reusable data table component with support for sorting, loading state, empty state, and row selection
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {boolean} props.loading - Loading state
 * @param {string} props.sortBy - Field to sort by
 * @param {string} props.sortOrder - Sort order (ASC or DESC)
 * @param {Function} props.onSort - Function to handle sorting
 * @param {Function} props.onRowClick - Optional function to handle row clicks
 * @param {string} props.selectedId - Optional ID of the selected row (for single selection)
 * @param {Array} props.selectedIds - Optional array of selected row IDs (for multiple selection)
 * @param {string} props.idField - Field to use as row ID (default: 'id')
 * @param {string} props.emptyMessage - Message to display when there's no data
 * @param {string} props.className - Additional CSS classes for the table container
 * @param {boolean} props.compact - Use compact padding (px-3 py-2 instead of px-6 py-4)
 */
const DataTable = ({
  columns,
  data,
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  selectedId,
  selectedIds = [],
  idField = 'id',
  emptyMessage,
  className = '',
  compact = false
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  
  const defaultEmptyMessage = replace('No data available');
  const displayEmptyMessage = emptyMessage || defaultEmptyMessage;
  
  const handleSort = (field) => {
    if (onSort) {
      onSort(field);
    }
  };

  const renderSortIndicator = (field) => {
    if (sortBy !== field) return null;
    return <span className="ml-1" aria-label={sortOrder === 'ASC' ? replace('Sorted ascending') : replace('Sorted descending')}>
      {sortOrder === 'ASC' ? '↑' : '↓'}
    </span>;
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const isRowSelected = (row) => {
    const rowId = row[idField];
    return selectedId === rowId || selectedIds.includes(rowId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingSpinner size="md" message="" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`text-center py-8 ${semantic.formText}`}>
        {displayEmptyMessage}
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className={`min-w-full divide-y ${semantic.contentCardBorder}`}>
        <thead className={semantic.sectionBg}>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.field || `column-${index}`}
                scope="col"
                className={`${compact ? 'px-3 py-2' : 'px-6 py-3'} text-xs font-medium ${semantic.formText} uppercase tracking-wider ${
                  column.sortable ? 'cursor-pointer hover:opacity-80' : ''
                } ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                onClick={column.sortable ? () => handleSort(column.field) : undefined}
                aria-sort={column.sortable && sortBy === column.field 
                  ? (sortOrder === 'ASC' ? 'ascending' : 'descending')
                  : 'none'
                }
              >
                {replace(column.header)}
                {column.sortable && renderSortIndicator(column.field)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={`${semantic.contentCardBg} divide-y ${semantic.contentCardBorder}`}>
          {data.map((row) => (
            <tr
              key={row[idField]}
              className={`transition-colors duration-150 ${
                isRowSelected(row) 
                  ? `${semantic.sectionBg} ${semantic.contentCardText}` 
                  : `${semantic.contentCardText} hover:${semantic.sectionBg}`
              } ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={onRowClick ? () => handleRowClick(row) : undefined}
              aria-selected={isRowSelected(row)}
            >
              {columns.map((column, colIndex) => (
                <td
                  key={`${row[idField]}-${column.field || `col-${colIndex}`}`}
                  className={`${compact ? 'px-3 py-2' : 'px-6 py-4'} ${
                    column.wrap ? '' : 'whitespace-nowrap'
                  } ${
                    column.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.render ? column.render(row) : row[column.field]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      wrap: PropTypes.bool,
      render: PropTypes.func
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['ASC', 'DESC']),
  onSort: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedIds: PropTypes.array,
  idField: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  compact: PropTypes.bool
};

export default DataTable;

